import React, { useEffect, useState } from "react";
import CustomTable from "../../components/customtable/CustomTable";
import DebounceSearch from "../../components/common/DebounceSearch";
import Breadcrumb from "../../components/common/Breadcrumb";
import Action from "../../components/common/Action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { deleteVideo, editVideo, getVideoList } from "../../redux/video/slice";
import { SortDownArrow } from "../../assets/svg";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { drag } from "../../assets/images";

const Video = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [videoDelete, setVideoDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [localVideos, setLocalVideos] = useState([]);

  // selector
  const { videoList, videoListLoading } = useSelector((store) => ({
    videoList: store?.videoData?.getVideoListStatus?.data?.data,
    videoListLoading: store?.videoData?.loading,
  }));

  // Fetch list
  useEffect(() => {
    dispatch(
      getVideoList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy || "sortingIndex",
        sort_direction: sortDirection === "desc" ? "desc" : "asc",
      })
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  // Sync local list when Redux list changes
  useEffect(() => {
    setLocalVideos(videoList?.data || []);
  }, [videoList]);

  // Delete video
  const deleteVideoId = () => {
    setIsLoading(true);
    if (!videoDelete) return;
    dispatch(deleteVideo(videoDelete)).then((res) => {
      setIsLoading(false);
      if (res.type === "deleteVideo/fulfilled") {
        dispatch(
          getVideoList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setConfirmDelete(false);
    });
  };
  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="w-16 table-th"></th>
      <th className="w-16 table-th">No.</th>
      <th className="w-56 table-th">
        <div
          className="flex items-center cursor-pointer gap-1 select-none"
          onClick={() => {
            setSortBy("videoName");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <span
            className={`transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "videoName"
                ? "rotate-180"
                : "rotate-0"
            }`}
          >
            <SortDownArrow />
          </span>
        </div>
      </th>
      <th className="table-th w-full">Video URL</th>
      <th className="w-24 table-th text-center">Actions</th>
    </>
  );

  // Table Rows with Drag & Drop
  const DataRows = () => {
    const [localVideos, setLocalVideos] = useState(videoList?.data || []);

    useEffect(() => {
      setLocalVideos(videoList?.data || []);
    }, [videoList]);

    const handleOnDragEnd = async (result) => {
      if (!result.destination) return;

      const reordered = Array.from(localVideos);
      const [movedItem] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, movedItem);
      setLocalVideos(reordered);

      // Only call API if position actually changed
      if (result.source.index !== result.destination.index) {
        const payload = {
          _id: movedItem._id,
          sortingIndex: result.destination.index,
        };

        try {
          const res = await dispatch(editVideo(payload)).unwrap();

          if (res?.status === "SUCCESS") {
            await dispatch(
              getVideoList({
                search: searchValue || "",
                pageCount: pageCount || 1,
                pageLimit: pageLimit || 10,
                sort_by: sortBy || "sortingIndex",
                sort_direction: "asc",
              })
            );
          }
        } catch (err) {
          console.error("Error updating order:", err);
        }
      }
    };

    return (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="video-table">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {localVideos.map((item, index) => (
                <Draggable key={item._id} draggableId={item._id} index={index}>
                  {(provided, snapshot) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`text-mgrey hover:bg-navTheme/5 transition-all duration-150 ${
                        snapshot.isDragging ? "bg-gray-100 shadow-md" : ""
                      }`}
                    >
                      <td>
                        <img
                          src={drag}
                          alt="drag"
                          className="w-5 h-5 mx-auto cursor-move"
                        />
                      </td>
                      <td className="px-5 py-3 text-center">
                        {(pageCount - 1) * pageLimit + index + 1}
                      </td>

                      <td className="px-5 py-3">
                        <div className="line-clamp-1">
                          {item?.videoName || "-"}
                        </div>
                      </td>

                      <td className="px-5 py-3 truncate" title={item?.videoUrl}>
                        {item?.videoUrl || "-"}
                      </td>

                      <td className="px-5 py-3 text-center">
                        <div className="flex justify-center">
                          <Action
                            optionList={
                              <>
                                <li
                                  className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                  onClick={() =>
                                    navigate("/add-edit-video", {
                                      state: { video: item, mode: "edit" },
                                    })
                                  }
                                >
                                  View / Edit
                                </li>
                                <li
                                  className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                  onClick={() => {
                                    setConfirmDelete(true);
                                    setVideoDelete(item?._id);
                                  }}
                                >
                                  Delete
                                </li>
                              </>
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">Video</h6>
        </div>

        <Breadcrumb
          breadCrumbParent={"Home"}
          breadCrumbActive={"Video"}
          breadCrumbTitleKey={"/"}
          classes=""
        />

        <div className="flex items-center space-x-4 w-full">
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <button
            className="bg-themeBtn text-white flex items-center justify-center px-2 py-[7px] rounded uppercase font-Proxima text-base w-40 shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            onClick={() => navigate("/add-edit-video")}
            type="button"
          >
            + Add
          </button>
        </div>

        <div className="w-full bg-white shadow rounded-lg">
          <CustomTable
            overflowVisibleLg={true}
            columnHeaders={<ColumnHeaders />}
            dataRows={<DataRows />}
            hasCustomBody={true}
            data={videoList}
            loading={videoListLoading}
            showPagination={true}
            disableNext={!!videoList?.data?.length}
            disablePrev={!!videoList?.data?.length}
            setPaginationCurrentLimit={setPageLimit}
            paginationCurrentLimit={pageLimit}
            current={pageCount}
            total={videoList?.pagination?.total}
            paginationData={(crPage) => setPageCount(crPage)}
            lastPage={Math.ceil(videoList?.pagination?.total / pageLimit)}
          />
        </div>
      </div>
      {confirmDelete && (
        <ConfirmationModel
          confirmText={"Delete"}
          onClose={() => setConfirmDelete(false)}
          onConfirm={deleteVideoId}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default Video;
